import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { remitosApi, notasEnvioApi } from '../api/remitos'
import { zonasApi } from '../api/zonas'
import EstadoVigenteBadge from '../components/EstadoVigenteBadge'
import EstadoChipsFilter from '../components/EstadoChipsFilter'
import { fmtFecha, toISOLocal } from '../utils/fecha'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { useStoredState } from '../hooks/useStoredState'
import Boton from '../components/Boton'
import Paginador from '../components/Paginador'
import { ESTADO_VIGENTE_OPCIONES } from '../design/estadosVigentes'

// Vocabulario canónico de estado_vigente (única fuente de verdad del backend).
// El filtro client-side compara contra estado_vigente.codigo de cada fila.
// La paleta vive en design/estadosVigentes.js (compartida con /m/logistica).
const ESTADO_OPCIONES = ESTADO_VIGENTE_OPCIONES

const TIPO_OPCIONES = [
  { value: '',    label: 'Todos' },
  { value: 't90', label: 'T90' },
  { value: 't80', label: 'T80' },
]

const PERIODO_PRESETS = [
  { value: '',       label: 'Todas' },
  { value: '7',      label: 'Últimos 7 días' },
  { value: '30',     label: 'Últimos 30 días' },
  { value: '90',     label: 'Últimos 90 días' },
  { value: 'custom', label: 'Personalizar' },
]

const FILTROS_DEFAULT = { q: '', estados: [], tipo: '', zona_id: '', periodo: '', fecha_desde: '', fecha_hasta: '' }

// Cuántas filas se DIBUJAN por página. La data se trae toda (filtro instantáneo
// client-side), pero solo renderizamos esta tanda — la sábana de 1.500 filas pesaba.
const POR_PAGINA = 30

// Devuelve fecha YYYY-MM-DD desplazada N días desde hoy
function fechaHaceDias(dias) {
  const d = new Date()
  d.setDate(d.getDate() - dias)
  return toISOLocal(d)
}

export default function LogisticaRemitosPage() {
  const navigate = useNavigate()
  const [remitos, setRemitos] = useState([])
  const [notasEnvio, setNotasEnvio] = useState([])
  const [zonas, setZonas] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filtros, setFiltros] = useStoredState('lgr.filtros.logistica.remitos', FILTROS_DEFAULT)
  const [pagina, setPagina] = useState(1)

  // Saneo: descarta estados guardados que ya no están en las opciones (chips que removimos).
  useEffect(() => {
    const validos = ESTADO_OPCIONES.map(o => o.value)
    const limpio = filtros.estados.filter(e => validos.includes(e))
    if (limpio.length !== filtros.estados.length) setFiltros(f => ({ ...f, estados: limpio }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Carga única: traemos los remitos de logística + sus NEs + zonas. El filtrado se
  // hace client-side sobre la lista unificada (cada fila por SUS propios valores), porque
  // la tabla mezcla remitos T90 y NEs T80 y cada uno tiene su propio estado/zona/número.
  const cargar = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    try {
      const [rRes, neRes, zRes] = await Promise.all([
        remitosApi.listar({ logistica: 1, per_page: 500, lean: 1 }),
        notasEnvioApi.listar({ per_page: 1000, lean: 1 }),
        zonasApi.listar(),
      ])
      // TODOS los remitos, de a 500: el filtro es client-side, y con solo los
      // últimos 500 un remito más viejo desaparecía (Martin, 31 oct 2026: en
      // distribución daba 24 en Ventas y 23 acá — faltaba el 1700008646 del 29/09).
      const ultima = rRes.data.last_page || 1
      const resto = ultima > 1
        ? await Promise.all(Array.from({ length: ultima - 1 }, (_, i) =>
            remitosApi.listar({ logistica: 1, per_page: 500, lean: 1, page: i + 2 })))
        : []
      setRemitos([rRes, ...resto].flatMap(r => r.data.data || []))
      const ultimaNe = neRes.data.last_page || 1
      const restoNe = ultimaNe > 1
        ? await Promise.all(Array.from({ length: ultimaNe - 1 }, (_, i) =>
            notasEnvioApi.listar({ per_page: 1000, lean: 1, page: i + 2 })))
        : []
      setNotasEnvio([neRes, ...restoNe].flatMap(r => r.data.data || []))
      setZonas(Array.isArray(zRes.data) ? zRes.data : (zRes.data.data || []))
      setError(null)
    } catch {
      setError('No se pudieron cargar los remitos de logística. Reintentá o limpiá los filtros.')
    } finally { if (!silent) setLoading(false) }
  }, [])

  useEffect(() => { cargar() }, [cargar])
  useAutoRefresh(() => cargar({ silent: true }), 30000)

  // Build unified items list
  const neByRemito = notasEnvio.reduce((acc, ne) => {
    if (!acc[ne.remito_id]) acc[ne.remito_id] = []
    acc[ne.remito_id].push(ne)
    return acc
  }, {})

  // Logística solo ve lo que está listo para repartir:
  // - T90 confirmados (entrega única, no necesitan NE)
  // - NEs de T80 ya generadas
  // Un T80 confirmado sin NE generada todavía es trabajo de ventas (armar planilla);
  // no se lista acá hasta que tenga al menos una NE.
  const items = []
  for (const r of remitos) {
    if (r.transporte === 80) {
      const nes = neByRemito[r.id] || []
      nes.forEach(ne => items.push({ key: `ne-${ne.id}`, tipo: 'ne', ne, remito: r }))
    } else {
      items.push({ key: `r-${r.id}`, tipo: 'remito', remito: r })
    }
  }

  // Accesores: cada fila se filtra por lo que REALMENTE muestra. El estado es el
  // canónico estado_vigente.codigo (única verdad del backend), no el escalar raw.
  const rowEstado = (i) => (i.tipo === 'ne' ? i.ne.estado_vigente?.codigo : i.remito.estado_vigente?.codigo)
  const rowZonaId = (i) => i.tipo === 'ne'
    ? (i.ne.zona?.id ?? i.remito.zona?.id ?? null)
    : (i.remito.zona?.id ?? null)
  const rowFecha  = (i) => (i.remito.fecha_emision || '').slice(0, 10)

  // Columna "Entrega" (la fecha REAL de entrega — sello del viaje / traza
  // Andreani): aparece solo al filtrar por entregados, que es cuando la
  // pregunta es "cuándo llegó" y no "cuándo se emitió" (Martin, 30 ago 2026).
  const verEntrega = filtros.estados.some(e => e === 'entregado' || e === 'recuperado')
  const rowEntrega = (i) => (i.tipo === 'ne' ? i.ne.entregado_el : i.remito.entregado_el)
  const rowMatchQ = (i, q) => {
    const s = q.trim().toLowerCase()
    const campos = i.tipo === 'ne'
      ? [i.ne.numero_ne, i.ne.destinatario?.nombre, i.remito.cliente?.nombre, i.remito.numero_presea]
      : [i.remito.numero_presea, i.remito.cliente?.nombre]
    return campos.some(v => (v ?? '').toString().toLowerCase().includes(s))
  }

  // Si hay un preset de período (7/30/90), el rango se calcula desde hoy hacia atrás;
  // 'custom' usa los inputs date de filtros.fecha_desde/hasta; '' = todas las fechas.
  const rangoFecha = (() => {
    if (filtros.periodo === 'custom') return { desde: filtros.fecha_desde, hasta: filtros.fecha_hasta }
    if (['7', '30', '90'].includes(filtros.periodo)) return { desde: fechaHaceDias(parseInt(filtros.periodo, 10)), hasta: '' }
    return { desde: '', hasta: '' }
  })()

  const itemsFiltrados = items.filter(i => {
    // Tipo (T90 / T80) — single-select, '' = todos
    if (filtros.tipo === 't90' && i.tipo !== 'remito') return false
    if (filtros.tipo === 't80' && i.tipo !== 'ne') return false
    // Estado (multi-selección) — por el estado propio de la fila
    if (filtros.estados.length && !filtros.estados.includes(rowEstado(i))) return false
    // Zona — por la zona propia de la fila
    if (filtros.zona_id && String(rowZonaId(i)) !== String(filtros.zona_id)) return false
    // Rango de fecha (sobre la fecha de emisión del remito)
    const f = rowFecha(i)
    if (rangoFecha.desde && f && f < rangoFecha.desde) return false
    if (rangoFecha.hasta && f && f > rangoFecha.hasta) return false
    // Búsqueda libre — nº remito/NE, cliente y destinatario
    if (filtros.q && !rowMatchQ(i, filtros.q)) return false
    return true
  })

  // Paginación SOLO de render: cuántas filas dibujamos. `paginaActual` clampea sola si la
  // lista filtrada se achicó (auto-refresh) por debajo de la página actual; cualquier
  // cambio de filtro vuelve a la página 1 vía `aplicar` (sin effects de setState).
  const totalPaginas = Math.max(1, Math.ceil(itemsFiltrados.length / POR_PAGINA))
  const paginaActual = Math.min(pagina, totalPaginas)
  const itemsPagina = itemsFiltrados.slice((paginaActual - 1) * POR_PAGINA, paginaActual * POR_PAGINA)

  // Toda mutación de filtros pasa por acá: actualiza + resetea a la página 1.
  const aplicar = (updater) => { setFiltros(updater); setPagina(1) }
  const setFiltro = (k) => (e) => aplicar(f => ({ ...f, [k]: e.target.value }))
  const toggle = (campo) => (val) => {
    aplicar(f => {
      const arr = f[campo]
      return { ...f, [campo]: arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val] }
    })
  }
  const limpiar = () => aplicar(FILTROS_DEFAULT)

  const hayFiltros = Boolean(
    filtros.q || filtros.tipo || filtros.zona_id || filtros.periodo || filtros.estados.length > 0
  )

  return (
    <div>
      <div style={st.header}>
        <h2 style={st.title}>Remitos — Logística</h2>
      </div>

      <div style={st.filtros}>
        <input style={st.fi} placeholder="Buscar nº, NE o cliente..." value={filtros.q} onChange={setFiltro('q')} />
        <select style={st.fi} value={filtros.zona_id} onChange={setFiltro('zona_id')}>
          <option value="">Todas las zonas</option>
          {zonas.map(z => <option key={z.id} value={z.id}>{z.nombre}</option>)}
        </select>
        <select style={st.fi} value={filtros.tipo} onChange={setFiltro('tipo')}>
          {TIPO_OPCIONES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div style={st.presetGroup}>
          {PERIODO_PRESETS.map(p => (
            <button
              key={p.value}
              type="button"
              onClick={() => aplicar(f => ({ ...f, periodo: p.value }))}
              style={{ ...st.presetBtn, ...(filtros.periodo === p.value ? st.presetBtnActive : {}) }}
            >
              {p.label}
            </button>
          ))}
        </div>
        {filtros.periodo === 'custom' && (
          <div style={st.customFechas}>
            <input style={st.fi} type="date" value={filtros.fecha_desde} onChange={setFiltro('fecha_desde')} />
            <span style={st.flecha}>→</span>
            <input style={st.fi} type="date" value={filtros.fecha_hasta} onChange={setFiltro('fecha_hasta')} />
          </div>
        )}
        <Boton variante={hayFiltros ? 'secondary' : 'ghost'} onClick={limpiar}>Limpiar</Boton>
      </div>
      {(() => {
        // "A revisar" = el bucket de varados (entrega fallida/reagendada sin reasignar).
        const revisarOn = filtros.estados.length === 1 && filtros.estados.includes('fallo_sin_reasignar')
        const toggleRevisar = () => {
          if (revisarOn) aplicar(f => ({ ...f, estados: [] }))
          else aplicar(f => ({ ...f, estados: ['fallo_sin_reasignar'] }))
        }
        return (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', margin: '0.4rem 0' }}>
            <button
              onClick={toggleRevisar}
              title="Filtra solo entregas fallidas o reagendadas — el bucket para recuperar"
              style={{
                padding: '5px 12px', borderRadius: 999, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                border: revisarOn ? '1px solid #c2410c' : '1px solid #e2e8f0',
                background: revisarOn ? '#fff7ed' : '#f8fafc',
                color: revisarOn ? '#c2410c' : '#475569',
                outline: 'none', transition: 'all 0.1s',
              }}
            >
              A revisar
            </button>
            <EstadoChipsFilter options={ESTADO_OPCIONES} selected={filtros.estados} onToggle={toggle('estados')} style={{ margin: 0 }} />
          </div>
        )
      })()}

      {!loading && !error && (
        <p style={st.info}>
          {itemsFiltrados.length} registro{itemsFiltrados.length !== 1 ? 's' : ''}
          {itemsFiltrados.length > POR_PAGINA && (
            <> · mostrando {(paginaActual - 1) * POR_PAGINA + 1}–{Math.min(paginaActual * POR_PAGINA, itemsFiltrados.length)}</>
          )}
          {hayFiltros && <strong style={st.filtrosActivos}> · filtros activos</strong>}
        </p>
      )}

      <div style={st.tableWrap}>
        <table style={st.table}>
          <thead>
            <tr>
              {['Nº', 'Fecha', 'Tipo', 'Código', 'Destinatario / Cliente', 'Zona', 'Estado', ...(verEntrega ? ['Entrega'] : []), ''].map((h, i) => (
                <th key={i} style={st.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={verEntrega ? 9 : 8} style={st.empty}>Cargando...</td></tr>
            ) : error ? (
              <tr><td colSpan={verEntrega ? 9 : 8} style={st.empty}>{error}</td></tr>
            ) : itemsFiltrados.length === 0 ? (
              <tr><td colSpan={verEntrega ? 9 : 8} style={st.empty}>
                {hayFiltros ? (
                  <>
                    <div>No hay remitos que coincidan con los filtros.</div>
                    <Boton variante="secondary" sm style={{ marginTop: 8 }} onClick={limpiar}>Limpiar filtros</Boton>
                  </>
                ) : 'Sin resultados.'}
              </td></tr>
            ) : itemsPagina.map(item => {
              if (item.tipo === 'remito') {
                const r = item.remito
                const tieneNumero = r.numero_presea && r.numero_presea !== '0'
                return (
                  <tr key={item.key} style={st.tr}>
                    <td style={st.tdMono}>
                      {tieneNumero ? r.numero_presea : <span style={st.placeholder}>—</span>}
                    </td>
                    <td style={st.tdFecha}>{fmtFecha(r.fecha_emision)}</td>
                    <td style={st.td}>
                      <span style={{ ...st.badge, ...st.t90 }}>T90</span>
                    </td>
                    <td style={st.tdMono}>{r.cliente?.codigo_presea || '—'}</td>
                    <td style={st.tdNombre}>
                      <span style={st.principal}>{r.cliente?.nombre}</span>
                    </td>
                    <td style={st.tdSec}>{r.zona?.nombre || <span style={st.noZona}>Sin zona</span>}</td>
                    <td style={st.td}><EstadoVigenteBadge estadoVigente={r.estado_vigente} /></td>
                    {verEntrega && <td style={st.tdFecha}>{rowEntrega(item) ? fmtFecha(rowEntrega(item)) : '—'}</td>}
                    <td style={st.tdAccion}>
                      <Boton variante="secondary" sm onClick={() => navigate(`/logistica/remitos/${r.id}`)}>Ver</Boton>
                    </td>
                  </tr>
                )
              }

              // tipo === 'ne' — destinatario arriba; remito padre + cliente abajo
              const { ne, remito: r } = item
              return (
                <tr key={item.key} style={st.trNe}>
                  <td style={st.tdMono}>{ne.numero_ne}</td>
                  <td style={st.tdFecha}>{fmtFecha(r.fecha_emision)}</td>
                  <td style={st.td}>
                    <span style={{ ...st.badge, ...st.tne }}>NE</span>
                  </td>
                  <td style={st.tdMono}>{r.cliente?.codigo_presea || '—'}</td>
                  <td style={st.tdNombre}>
                    <div style={st.principal}>{ne.destinatario?.nombre || '—'}</div>
                    <div style={st.padreBlock}>
                      <span style={st.padreGlifo}>↳</span>
                      <span style={st.padreNumero}>{r.numero_presea}</span>
                      <span style={st.padreCliente}>{r.cliente?.nombre}</span>
                    </div>
                  </td>
                  <td style={st.tdSec}>{ne.zona?.nombre || r.zona?.nombre || <span style={st.noZona}>Sin zona</span>}</td>
                  <td style={st.td}><EstadoVigenteBadge estadoVigente={ne.estado_vigente} /></td>
                  {verEntrega && <td style={st.tdFecha}>{rowEntrega(item) ? fmtFecha(rowEntrega(item)) : '—'}</td>}
                  <td style={st.tdAccion}>
                    <Boton variante="secondary" sm onClick={() => navigate(`/logistica/notas-envio/${ne.id}`)}>Ver</Boton>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {!loading && !error && (
        <Paginador pagina={paginaActual} totalPaginas={totalPaginas} onCambiar={setPagina} />
      )}
    </div>
  )
}

const st = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' },
  title: { margin: 0, fontSize: '1.25rem', color: '#1e293b' },
  filtros: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.5rem', background: 'white', padding: '0.75rem', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  fi: { padding: '0.4rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', minWidth: '130px' },
  presetGroup: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  presetBtn: { padding: '5px 12px', borderRadius: 999, border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', color: '#475569' },
  presetBtnActive: { background: '#dbeafe', color: '#1e40af', border: '1px solid #1e40af' },
  customFechas: { display: 'flex', gap: 8, alignItems: 'center' },
  flecha: { color: '#94a3b8', fontSize: '0.8rem' },
  info: { margin: '0 0 0.5rem', color: '#64748b', fontSize: '0.8125rem' },
  filtrosActivos: { color: '#1d4ed8', fontWeight: 600 },
  // Sistema tipográfico Linear: mono para identificadores (Nº, Código),
  // sans para nombres humanos. Una sola línea por fila — altura uniforme.
  tableWrap: { background: 'white', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'auto', fontVariantNumeric: 'tabular-nums' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { padding: '8px 16px', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: 600, fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f1f5f9', height: 44 },
  trNe: { borderBottom: '1px solid #f1f5f9', height: 56 },
  td: { padding: '0 16px', verticalAlign: 'middle', fontSize: '13px', color: '#0f172a', lineHeight: 1.2 },
  tdMono: { padding: '0 16px', verticalAlign: 'middle', fontSize: '13px', color: '#0f172a', fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontWeight: 500, whiteSpace: 'nowrap', lineHeight: 1.2 },
  tdFecha: { padding: '0 16px', verticalAlign: 'middle', fontSize: '12px', color: '#64748b', fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', whiteSpace: 'nowrap', lineHeight: 1.2 },
  tdSec: { padding: '0 16px', verticalAlign: 'middle', fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap', lineHeight: 1.2 },
  tdNombre: { padding: '0 16px', verticalAlign: 'middle', maxWidth: 380, overflow: 'hidden', lineHeight: 1.2 },
  tdAccion: { padding: '0 16px', verticalAlign: 'middle', textAlign: 'right' },
  principal: { fontSize: '13px', fontWeight: 500, color: '#0f172a' },
  // Padre del NE en segunda línea, contexto secundario
  padreBlock: { marginTop: 2, fontSize: '12px', fontWeight: 400, color: '#64748b', display: 'flex', alignItems: 'baseline', gap: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  padreGlifo: { color: '#cbd5e1', fontSize: '12px' },
  padreNumero: { fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', color: '#64748b' },
  padreCliente: { color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis' },
  empty: { padding: '2rem', textAlign: 'center', color: '#94a3b8' },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: '11px', fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase' },
  t90: { background: '#eff6ff', color: '#1d4ed8' },
  tne: { background: '#f5f3ff', color: '#6d28d9' },
  noZona: { color: '#cbd5e1', fontSize: '12px' },
  placeholder: { color: '#cbd5e1' },
  paginador: { display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center', marginTop: '0.75rem' },
  pagBtn: { padding: '6px 14px', borderRadius: 999, border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', color: '#475569' },
  pagBtnOff: { opacity: 0.45, cursor: 'default' },
  pagInfo: { fontSize: '0.8125rem', color: '#64748b', fontVariantNumeric: 'tabular-nums' },
}
